import type { LaborRateGridLibrary } from '../../api/dealer-api-interface-all';
import type { ResultGetLaborRateMatrix } from '../../../webmodule-common/interop/interfaces/v6-config';

// Only a loaded matrix carries a supplier and a result, so a save cannot start without both.
export type MatrixLoad =
  | { state: 'loaded'; supplierId: string; loaded: ResultGetLaborRateMatrix }
  | { state: 'supplierOffline' | 'unsupported' | 'failed' };

// Why take the two reads as parameters? They are the only impure part of loading the tab, and passing
// them in is what lets the offline, failed and thrown paths be tested without a server.
export async function loadLaborRateMatrix(
  getSuppliers: () => Promise<{ id: string; online: boolean }[] | null>,
  getMatrix: (supplierId: string) => Promise<ResultGetLaborRateMatrix | null>
): Promise<MatrixLoad> {
  // Why catch at all? If either read throws (a network failure, say), the tab would otherwise stay on
  // its loading message for good, and re-entering it would fail the same way. A response the server
  // rejected (such as a supplier system below the macro floor) has already been shown by the global
  // response handler, so both a throw and a null only need the failed state.
  try {
    const supplierId = (await getSuppliers())?.find(supplier => supplier.online)?.id;
    if (!supplierId) return { state: 'supplierOffline' };
    const loaded = await getMatrix(supplierId);
    return loaded ? { state: 'loaded', supplierId, loaded } : { state: 'failed' };
  } catch {
    return { state: 'failed' };
  }
}

export interface MatrixSelectionCodes {
  libraryCode: string;
  layerCode: string;
}

export interface MatrixSelection {
  libraryIndex: number;
  layerIndex: number;
}

// Why resolve by code rather than keep the indices? The matrix is re-read from V6 on every entry, so a
// library removed ahead of the selected one would leave the old index valid but pointing at a different
// library, and the user would carry on editing it without any sign. A code that is gone falls back to
// the first entry; a layer is only kept when its library still is, as layers belong to their library.
export function resolveSelection(
  libraries: LaborRateGridLibrary[],
  previous: MatrixSelectionCodes | null
): MatrixSelection {
  const first: MatrixSelection = { libraryIndex: 0, layerIndex: 0 };
  if (!previous) return first;
  const libraryIndex = libraries.findIndex(library => library.libraryCode === previous.libraryCode);
  if (libraryIndex === -1) return first;
  return { libraryIndex, layerIndex: Math.max(0, libraries[libraryIndex].layers.indexOf(previous.layerCode)) };
}
